import { useState, useMemo } from "react";
import { Plus, Trash2, AlertCircle, AlertTriangle, Clock } from "lucide-react";
import { dateLabel } from "@/lib/format";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

// Auto-compute urgency from days-until-expiry. Returns the bucket *and*
// the days-remaining so the tooltip can be specific ("Expires in 15 days").
const autoUrgency = (iso) => {
  if (!iso || iso === "no expiry") return { bucket: "ok", days: null };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { bucket: "ok", days: null };
  const days = Math.floor((d.getTime() - Date.now()) / 86400000);
  if (days < 0)   return { bucket: "expired", days };
  if (days <= 30) return { bucket: "urgent",  days };
  if (days <= 90) return { bucket: "due",     days };
  return { bucket: "ok", days };
};

// Icon + Radix tooltip on hover. Only renders when the row needs
// attention; "ok" rows are unbadged.
function UrgencyIcon({ urgency, expiresAt }) {
  if (!urgency || urgency.bucket === "ok") return null;
  const { bucket, days } = urgency;
  let Icon, iconCls, contentCls, headline, sub;
  const formatted = expiresAt ? dateLabel(expiresAt) : null;
  if (bucket === "expired") {
    Icon = AlertCircle;
    iconCls = "text-red-700";
    contentCls = "bg-red-700 text-white";
    headline = days === -1 ? "Expired 1 day ago" : `Expired ${Math.abs(days)} days ago`;
  } else if (bucket === "urgent") {
    Icon = AlertTriangle;
    iconCls = "text-red-700";
    contentCls = "bg-red-700 text-white";
    headline = days === 0 ? "Expires today" : days === 1 ? "Expires tomorrow" : `Expires in ${days} days`;
  } else {
    Icon = Clock;
    iconCls = "text-amber-700";
    contentCls = "bg-amber-700 text-white";
    headline = `Expires in ${days} days`;
  }
  if (formatted) sub = formatted;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`shrink-0 ${iconCls} cursor-help`} aria-label={headline}>
            <Icon size={14} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className={`${contentCls} border-none shadow-lg`}>
          <div className="font-semibold">{headline}</div>
          {sub && <div className="opacity-90 text-[10.5px] mt-0.5">{sub}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const CATEGORY_OPTIONS = ["hotel", "airline", "bank", "other"];

// Items with no expiry sort to the bottom; expired items float to the top.
const sortKey = (iso) => {
  if (!iso || iso === "no expiry") return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

export default function UseThisNow({
  credits,
  programs,
  awards = [],
  onAddCredit,
  onDeleteCredit,
  onDeleteAward,
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: "",
    sublabel: "",
    value: "",
    expires_at: "",
    category: "other",
  });

  // Build a unified, single-chronological list of credits + awards.
  const items = useMemo(() => {
    const programById = new Map((programs || []).map((p) => [p.id, p]));

    const creditRows = (credits || []).map((c) => ({
      id: `c:${c.id}`,
      kind: "credit",
      raw_id: c.id,
      label: c.label,
      sublabel: c.sublabel || "",
      value: c.value || "",
      expires_at: c.expires_at,
      urgency: autoUrgency(c.expires_at),
      category: CATEGORY_OPTIONS.includes(c.category) ? c.category : "other",
    }));

    const awardRows = (awards || []).map((a) => {
      const p = programById.get(a.program_id);
      const pcat = CATEGORY_OPTIONS.includes(p?.category) ? p.category : "other";
      const sub = [p?.name, a.award_type].filter(Boolean).join(" · ");
      return {
        id: `a:${a.id}`,
        kind: "award",
        raw_id: a.id,
        label: a.label,
        sublabel: sub,
        value: a.notes || "",
        expires_at: a.expires_at,
        urgency: autoUrgency(a.expires_at),
        category: pcat,
      };
    });

    return [...creditRows, ...awardRows]
      .filter((r) => r.urgency?.bucket !== "expired")
      .sort((x, y) => sortKey(x.expires_at) - sortKey(y.expires_at));
  }, [credits, programs, awards]);

  const submit = async (e) => {
    e.preventDefault();
    await onAddCredit(form);
    setForm({ label: "", sublabel: "", value: "", expires_at: "", category: "other" });
    setShowForm(false);
  };

  const handleDelete = async (row) => {
    if (row.kind === "credit") return onDeleteCredit(row.raw_id);
    if (row.kind === "award") return onDeleteAward(row.raw_id);
  };

  return (
    <section className="card p-6" data-testid="use-this-now">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="section-eyebrow">Use this now</div>
          <h2 className="font-display text-xl font-extrabold mt-1 tracking-tight">
            Cash, credits & expiring awards
          </h2>
          <div className="text-[12px] text-neutral-500 mt-1">
            {items.length} item{items.length === 1 ? "" : "s"} · sorted by expiry, soonest first
          </div>
        </div>
        <button
          data-testid="add-credit-btn"
          onClick={() => setShowForm((v) => !v)}
          className="btn btn-ghost text-[13px]"
        >
          <Plus size={14} /> Add credit
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-4 p-4 border border-black/10 rounded-[10px] bg-black/[0.02] space-y-3">
          <input
            required
            className="input"
            placeholder="Label (e.g. Bilt hotel credit)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            data-testid="credit-label-input"
          />
          <input
            className="input"
            placeholder="Sub-label (Mihir · Bilt Travel portal)"
            value={form.sublabel}
            onChange={(e) => setForm({ ...form, sublabel: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              required
              className="input"
              placeholder='Value ($200, ~$400)'
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              data-testid="credit-value-input"
            />
            <input
              className="input"
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            />
          </div>
          <select
            className="input"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="credit-category-select"
          >
            <option value="hotel">Hotel</option>
            <option value="airline">Airline</option>
            <option value="bank">Credit card</option>
            <option value="other">Other</option>
          </select>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-ghost text-[13px]" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary text-[13px]" data-testid="credit-save-btn">
              Save credit
            </button>
          </div>
        </form>
      )}

      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
        {items.length === 0 ? (
          <div className="text-[13px] text-neutral-500 py-8 text-center border border-dashed border-black/10 rounded-[10px]">
            No credits or expiring awards yet. Add credits above, or add awards under each program card.
          </div>
        ) : (
          items.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-2.5 px-3 py-2.5 border border-black/5 rounded-[10px] hover:bg-black/[0.02] transition-colors group"
              data-testid={`use-now-row-${r.id}`}
            >
              <UrgencyIcon urgency={r.urgency} expiresAt={r.expires_at} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[13.5px] truncate">{r.label}</div>
                {r.sublabel && (
                  <div className="text-[12px] text-neutral-500 truncate">{r.sublabel}</div>
                )}
              </div>
              <div className="text-right shrink-0">
                {r.value && (
                  <div className="tabular font-semibold text-[13.5px]">{r.value}</div>
                )}
                <div
                  className={`text-[11px] tabular ${
                    r.urgency?.bucket === "urgent" || r.urgency?.bucket === "expired"
                      ? "text-red-700 font-semibold"
                      : r.urgency?.bucket === "due"
                      ? "text-amber-700 font-semibold"
                      : "text-neutral-500"
                  }`}
                >
                  {dateLabel(r.expires_at)}
                </div>
              </div>
              <button
                onClick={() => handleDelete(r)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-red-700"
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
